import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Upload,
  Trash2,
  Image as ImageIcon,
  Loader2,
  Monitor,
  Tablet,
  Smartphone,
  Share2,
  Gift,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { resolveImageUrl } from "@/lib/images";

const MAX_INPUT_MB = 10;

const VARIANTS = {
  mobile: {
    targetW: 1080,
    targetH: 1920,
    aspectClass: "aspect-[9/16] max-w-[180px]",
    Icon: Smartphone,
  },
  tablet: {
    targetW: 1536,
    targetH: 2048,
    aspectClass: "aspect-[3/4] max-w-[180px]",
    Icon: Tablet,
  },
  desktop: {
    targetW: 1920,
    targetH: 1080,
    aspectClass: "aspect-video max-w-[240px]",
    Icon: Monitor,
  },
  og: {
    targetW: 1200,
    targetH: 630,
    aspectClass: "aspect-[1200/630] max-w-[240px]",
    Icon: Share2,
  },
  win: {
    targetW: 1000,
    targetH: 1000,
    aspectClass: "aspect-square max-w-[170px]",
    Icon: Gift,
  },
};

async function resizeToBlob(file, targetW, targetH) {
  const img = document.createElement("img");
  const objUrl = URL.createObjectURL(file);
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
    img.src = objUrl;
  });
  const scale = Math.min(targetW / img.width, targetH / img.height, 1);
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  canvas.getContext("2d").drawImage(img, 0, 0, w, h);
  URL.revokeObjectURL(objUrl);
  return await new Promise((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", 0.85)
  );
}

export default function BackgroundUploader({
  value,
  onChange,
  variant = "mobile",
  label = null,
  testIdPrefix = "bg",
  allowGif = false,
}) {
  const { t } = useTranslation();
  const inputRef = useRef();
  const [loading, setLoading] = useState(false);
  const cfg = VARIANTS[variant] || VARIANTS.mobile;

  async function onFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_INPUT_MB * 1024 * 1024) {
      toast.error(t("bg.too_large", { mb: MAX_INPUT_MB }));
      return;
    }
    const isGif = /^image\/gif/i.test(file.type);
    if (!/^image\/(jpeg|png|webp|jpg)/i.test(file.type) && !(isGif && allowGif)) {
      toast.error(t("bg.wrong_format"));
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      if (isGif) {
        fd.append("file", file, "animation.gif");
      } else {
        const blob = await resizeToBlob(file, cfg.targetW, cfg.targetH);
        fd.append("file", blob, "background.jpg");
      }
      const { data } = await api.post("/uploads/game-background", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success(t("bg.uploaded"));
    } catch (err) {
      toast.error(formatApiError(err) || t("bg.process_error"));
    } finally {
      setLoading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  const preview = resolveImageUrl(value);
  const { Icon } = cfg;

  return (
    <div>
      <div className="text-xs font-medium mb-1.5 flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
        {label || t("bg.label")}
      </div>
      <div
        className={`relative ${cfg.aspectClass} rounded-xl border border-dashed border-border bg-muted/40 overflow-hidden`}
        data-testid={`${testIdPrefix}-uploader-preview`}
      >
        {preview ? (
          <img
            src={preview}
            alt="background preview"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground gap-1.5">
            <ImageIcon className="h-6 w-6" />
            <div className="text-[10px] tracking-wide text-center px-2 leading-tight">
              {t("bg.empty_hint")}
            </div>
          </div>
        )}
        {loading && (
          <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        )}
      </div>

      <div className="mt-2 flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          className="h-8 rounded-lg text-xs"
          onClick={() => inputRef.current?.click()}
          disabled={loading}
          data-testid={`${testIdPrefix}-upload-btn`}
          type="button"
        >
          <Upload className="h-3 w-3 mr-1.5" />
          {value ? t("bg.replace") : t("bg.upload")}
        </Button>
        {value && (
          <Button
            size="sm"
            variant="ghost"
            className="h-8 rounded-lg text-xs text-destructive hover:text-destructive"
            onClick={() => onChange(null)}
            disabled={loading}
            data-testid={`${testIdPrefix}-remove-btn`}
            type="button"
          >
            <Trash2 className="h-3 w-3 mr-1.5" />
            {t("common.delete")}
          </Button>
        )}
      </div>
      <div className="mt-1 text-[10px] text-muted-foreground leading-relaxed">
        {variant === "mobile"
          ? t("bg.spec_hint")
          : t(`bg.spec_hint_${variant}`)}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={
          allowGif
            ? "image/jpeg,image/png,image/webp,image/gif"
            : "image/jpeg,image/png,image/webp"
        }
        onChange={onFile}
        className="hidden"
        data-testid={`${testIdPrefix}-file-input`}
      />
    </div>
  );
}
